/**
 * AI Recruit - Version Update Notifier
 * Polls the build's version.json and prompts the user to refresh when a
 * newer deployment is detected, instead of silently running a stale build.
 */

import React, { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { ArrowUpCircleIcon } from '@heroicons/react/24/outline'

const VERSION_URL = '/version.json'
const CHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const TOAST_ID = 'app-update-available'

async function fetchVersion(): Promise<string | null> {
  try {
    // cache: 'no-store' bypasses the browser's HTTP cache entirely so a stale
    // service worker or intermediary can't mask a new deployment
    const response = await fetch(`${VERSION_URL}?ts=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return null
    const data = await response.json()
    return typeof data?.version === 'string' ? data.version : null
  } catch {
    // Network hiccup or (in local dev) no version.json at all - just skip this check
    return null
  }
}

const VersionUpdateNotifier: React.FC = () => {
  const knownVersionRef = useRef<string | null>(null)
  const notifiedRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    const checkForUpdate = async () => {
      const latest = await fetchVersion()
      if (cancelled || !latest) return

      if (knownVersionRef.current === null) {
        knownVersionRef.current = latest
        return
      }

      if (latest !== knownVersionRef.current && !notifiedRef.current) {
        notifiedRef.current = true
        toast(
          (t) => (
            <div className="flex gap-3 w-[300px]">
              <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-full bg-blue-50">
                <ArrowUpCircleIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Update available</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  A new version of AI Recruit is ready.
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <button
                    onClick={() => window.location.reload()}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    Refresh now
                  </button>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Later
                  </button>
                </div>
              </div>
            </div>
          ),
          { id: TOAST_ID, duration: Infinity }
        )
      }
    }

    checkForUpdate()

    const interval = setInterval(checkForUpdate, CHECK_INTERVAL_MS)

    // Also check whenever the user comes back to this tab - catches deploys
    // that happened while the tab was in the background
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [])

  return null
}

export default VersionUpdateNotifier
